const normalizeRoleKey = (value) => {
  if (value === null || value === undefined) {
    return ''
  }

  return String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
}

const ROLE_ALIASES = Object.freeze({
  administrator: 'admin',
  super_admin: 'admin',
  superadmin: 'admin',
  superuser: 'admin',
  team_lead: 'manager',
  teamlead: 'manager',
  lead: 'manager',
  agent: 'associate',
  associate: 'associate',
  advisor: 'associate', // legacy: treat advisor as associate (Associate or above)
  representative: 'representative',
  rep: 'representative',
  field_associate: 'field_associate',
  fieldassociate: 'field_associate',
  senior_associate: 'senior_associate',
  seniorassociate: 'senior_associate',
  sr_associate: 'senior_associate',
  marketing_director: 'marketing_director',
  marketingdirector: 'marketing_director',
  sr_marketing_director: 'smd',
  senior_marketing_director: 'smd',
  senior_marketingdirector: 'smd',
  sr_marketingdirector: 'smd',
  smd: 'smd',
  executive_marketing_director: 'emd',
  executive_marketingdirector: 'emd',
  executive_md: 'emd',
  executive_director: 'emd',
  emd: 'emd',
  newassociate: 'new_associate',
  new_associate: 'new_associate',
  client: 'client',
  customer: 'client'
})

export const mapLegacyRole = (value) => {
  const normalizedKey = normalizeRoleKey(value)

  if (!normalizedKey) {
    return null
  }

  return ROLE_ALIASES[normalizedKey] ?? normalizedKey
}

export const ADMIN_ROLES = new Set(['admin'])
const MANAGER_CORE_ROLES = new Set(['manager', 'marketing_director', 'smd', 'emd'])
export const MANAGER_LEVEL_ROLES = new Set([...MANAGER_CORE_ROLES, ...ADMIN_ROLES])

// Associate and above (licensed); Representative is separate (unlicensed / in training)
const ADVISOR_CORE_ROLES = new Set([
  'associate',
  'field_associate',
  'senior_associate'
])

export const ADVISOR_LEVEL_ROLES = new Set([
  ...ADVISOR_CORE_ROLES,
  ...MANAGER_LEVEL_ROLES
])

export const CLIENT_AND_ABOVE_ROLES = new Set(['client', 'representative', ...ADVISOR_LEVEL_ROLES])
export const NEW_ASSOCIATE_ROLES = new Set(['new_associate'])

export const LICENSED_ROLES = new Set([
  'associate',
  'senior_associate',
  'manager',
  'marketing_director',
  'smd',
  'emd',
  'admin'
])

export const UNLICENSED_ROLES = new Set([
  'representative',
  'new_associate'
])

export const isEMDOrAdmin = (value) => {
  const role = mapLegacyRole(value)
  if (!role) {
    return false
  }

  return role === 'emd' || role === 'admin'
}

export const isManagerLevel = (value) => {
  const role = mapLegacyRole(value)
  if (!role) {
    return false
  }

  return MANAGER_LEVEL_ROLES.has(role)
}

export const isAdvisorLevel = (value) => {
  const role = mapLegacyRole(value)
  if (!role) {
    return false
  }

  return ADVISOR_LEVEL_ROLES.has(role)
}

export const createRoleSet = (collection) => {
  if (!collection) {
    return new Set()
  }

  const roles = Array.isArray(collection) ? collection : [...collection]
  const normalized = new Set()

  roles.forEach((entry) => {
    const role = mapLegacyRole(entry)
    if (role) {
      normalized.add(role)
    }
  })

  return normalized
}

export const isLicensed = (value) => {
  const role = mapLegacyRole(value)
  if (!role) {
    return false
  }

  return LICENSED_ROLES.has(role)
}

export const isUnlicensed = (value) => {
  const role = mapLegacyRole(value)
  if (!role) {
    return false
  }

  return UNLICENSED_ROLES.has(role)
}

export const isRoleAllowed = (allowedRoles, value) => {
  const role = mapLegacyRole(value)
  if (!role) {
    return false
  }

  if (!allowedRoles) {
    return true
  }

  if (typeof allowedRoles === 'function') {
    return Boolean(allowedRoles(role))
  }

  if (allowedRoles instanceof Set) {
    if (allowedRoles.size===0) {
      return true
    }

    return allowedRoles.has(role)
  }

  if (Array.isArray(allowedRoles)) {
    if (allowedRoles.length===0) {
      return true
    }

    return createRoleSet(allowedRoles).has(role)
  }

  const normalizedAllowed = mapLegacyRole(allowedRoles)
  if (!normalizedAllowed) {
    return false
  }

  return role === normalizedAllowed
}

export const ROLE_HELPERS = {
  ADMIN_ROLES,
  MANAGER_LEVEL_ROLES,
  ADVISOR_LEVEL_ROLES,
  CLIENT_AND_ABOVE_ROLES,
  NEW_ASSOCIATE_ROLES,
  LICENSED_ROLES,
  UNLICENSED_ROLES
}

export default ROLE_HELPERS
